import { useRequest } from 'features/request/useRequest';

import { useMemo } from 'react';

import type { IVariablesChangeData } from '../../WorkOrderUseCoefficient';
import {
    uerRatioUerRowItemListForWorkOrderObjects,
    uerRatioUerRowItemListForWorkOrderRootObject,
} from '../../workOrderUseCoefficientQueries';

interface IRatioUerRowItemsData {
    ratioRowId?: number;
    constructionTypeId?: number;
    isRootItem?: boolean;
    workOrderChangeId?: number;
    variablesChangeData: IVariablesChangeData;
    isWorkOrderChange?: boolean;
}

export const useRatioUerRowChangeItemsData = (props: IRatioUerRowItemsData) => {
    const { ratioRowId, isRootItem, variablesChangeData, isWorkOrderChange } = props;

    const variablesForRootObject = useMemo(
        () => ({
            workOrderChangeId: variablesChangeData.workOrderChangeId,
            ratioRowId,
            rootObjectIds: {
                ...variablesChangeData?.rootObjectIds,
            },
            constructionTypeId: variablesChangeData.constructionTypeId,
        }),
        [
            ratioRowId,
            variablesChangeData.constructionTypeId,
            variablesChangeData?.rootObjectIds,
            variablesChangeData.workOrderChangeId,
        ],
    );

    const variablesForObjects = useMemo(
        () => ({
            ratioRowId,
            workOrderChangeId: variablesChangeData.workOrderChangeId,
            objectIds: variablesChangeData?.objectIds || [],
            constructionTypeId: variablesChangeData.constructionTypeId,
        }),
        [
            ratioRowId,
            variablesChangeData.constructionTypeId,
            variablesChangeData?.objectIds,
            variablesChangeData.workOrderChangeId,
        ],
    );

    const [ratioRowItemsRootObjectData, ratioRowItemsRootObjectStatus, ,] = useRequest({
        requestId: 'uerRatioUerRowItemListForWorkOrderRootObject',
        query: uerRatioUerRowItemListForWorkOrderRootObject,
        withEffect: isRootItem && isWorkOrderChange,
        variables: variablesForRootObject,
    });

    const [ratioRowItemsObjectData, ratioRowItemsObjectStatus, ,] = useRequest({
        requestId: 'uerRatioUerRowItemListForWorkOrderObjects',
        query: uerRatioUerRowItemListForWorkOrderObjects,
        withEffect: !isRootItem && isWorkOrderChange,
        variables: variablesForObjects,
    });

    const rowOrderChangeData = useMemo(
        () =>
            (isRootItem && ratioRowItemsRootObjectData?.uerRatioUerRowItemListForWorkOrderRootObject?.items) ||
            (!isRootItem && ratioRowItemsObjectData?.uerRatioUerRowItemListForWorkOrderObjects?.items) ||
            [],
        [
            isRootItem,
            ratioRowItemsRootObjectData?.uerRatioUerRowItemListForWorkOrderRootObject?.items,
            ratioRowItemsObjectData?.uerRatioUerRowItemListForWorkOrderObjects?.items,
        ],
    );

    const rowOrderChangeFetchingStatus = useMemo(
        () => (isRootItem && ratioRowItemsRootObjectStatus) || (!isRootItem && ratioRowItemsObjectStatus),
        [isRootItem, ratioRowItemsObjectStatus, ratioRowItemsRootObjectStatus],
    );

    return {
        rowOrderChangeData,
        rowOrderChangeFetchingStatus,
    };
};
